import React, { createContext, useContext, useState, useCallback } from 'react';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const addNotification = useCallback((notification) => {
    const id = Date.now() + Math.random();
    const newNotification = {
      id,
      ...notification,
      timestamp: new Date()
    };
    
    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove after 2 seconds unless it's persistent
    if (!notification.persistent) {
      setTimeout(() => {
        removeNotification(id);
      }, 2000);
    }

    return id;
  }, [removeNotification]);

  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({
      type: 'success',
      message,
      ...options
    });
  }, [addNotification]);

  const showError = useCallback((message, options = {}) => {
    return addNotification({
      type: 'error',
      message,
      ...options
    });
  }, [addNotification]);

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({
      type: 'info',
      message,
      ...options
    });
  }, [addNotification]);

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({
      type: 'warning',
      message,
      ...options
    });
  }, [addNotification]);

  const showMockupGenerated = useCallback((remaining) => {
    return showSuccess(
      `Mockup generated successfully! You have ${remaining} mockups remaining.`,
      { persistent: false }
    );
  }, [showSuccess]);

  const showMockupExhausted = useCallback((onUpgrade) => {
    return addNotification({
      type: 'warning',
      message: 'You have run out of mockups!',
      description: 'Purchase more mockups or upgrade your plan to continue generating.',
      persistent: true,
      actions: [
        {
          label: 'Upgrade Plan',
          onClick: onUpgrade,
          variant: 'primary'
        }
      ]
    });
  }, [addNotification]);

  const showPurchaseSuccess = useCallback((mockupsAdded, totalMockups) => {
    return showSuccess(
      `Purchase successful! ${mockupsAdded} mockups added. You now have ${totalMockups} mockups.`,
      { persistent: false }
    );
  }, [showSuccess]);

  const showSubscriptionSuccess = useCallback((planName) => {
    return showSuccess(
      `Subscription activated! You now have access to ${planName}.`,
      { persistent: false }
    );
  }, [showSuccess]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    showSuccess,
    showError,
    showInfo,
    showWarning,
    showMockupGenerated,
    showMockupExhausted,
    showPurchaseSuccess,
    showSubscriptionSuccess
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

const NotificationContainer = () => {
  const { notifications, removeNotification } = useNotifications();

  if (notifications.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      {notifications.map((notification) => (
        <NotificationItem
          key={notification.id}
          notification={notification}
          onClose={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
};

const NotificationItem = ({ notification, onClose }) => {
  return (
    <div className="bg-background rounded-lg p-6 w-full max-w-md mx-4">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        {notification.message}
      </h3>
      {notification.description && (
        <p className="text-sm text-muted-foreground mb-6">
          {notification.description}
        </p>
      )}
      {notification.actions && (
        <div className="flex gap-3 justify-end">
          {notification.actions.map((action, index) => (
            <button
              key={index}
              onClick={() => {
                onClose(); // Remove notification immediately
                action.onClick(); // Then execute the action
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                action.variant === 'primary'
                  ? 'text-white bg-red-600 hover:bg-red-700'
                  : 'text-foreground bg-muted hover:bg-muted/80'
              }`}
            >
              {action.icon && <action.icon className="w-3 h-3 mr-1" />}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationProvider;
